import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../database/firebase";
import {
  ArrowLeft,
  TrendingUp,
  TrendingDown,
  Activity,
  Moon,
  Flame,
  Droplets,
  Scale,
  Heart,
  Target,
  Award,
  Calendar,
} from "lucide-react";

const TrackerAnalytics = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [timeRange, setTimeRange] = useState("30"); // 7, 30, 90 days
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadAnalytics();
    }
  }, [user, timeRange]);

  const loadAnalytics = async () => {
    if (!user?.uid) return;

    setIsLoading(true);
    try {
      const userDoc = await getDoc(doc(db, "users", user.uid));
      if (userDoc.exists()) {
        const userData = userDoc.data();
        const records = userData.healthInfo?.dailyTracking?.records || [];

        // Filter records by time range
        const daysAgo = parseInt(timeRange);
        const cutoffDate = new Date();
        cutoffDate.setDate(cutoffDate.getDate() - daysAgo);

        const filteredRecords = records.filter((r) => {
          const recordDate = new Date(r.date);
          return recordDate >= cutoffDate;
        });

        // Calculate analytics
        const analytics = calculateAnalytics(filteredRecords, userData);
        setAnalyticsData(analytics);
      }
    } catch (error) {
      console.error("Error loading analytics:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateAnalytics = (records, userData) => {
    if (records.length === 0) {
      return {
        summary: {
          totalDays: 0,
          trackedDays: 0,
          streak: 0,
        },
        averages: {},
        trends: {},
        goals: {},
      };
    }

    // Calculate averages
    const totalSteps = records.reduce((sum, r) => sum + (r.steps || 0), 0);
    const totalSleep = records.reduce(
      (sum, r) => sum + (r.sleep?.duration || 0),
      0
    );
    const totalCalories = records.reduce(
      (sum, r) => sum + (r.calories?.intake || 0),
      0
    );
    const totalWater = records.reduce((sum, r) => sum + (r.water || 0), 0);
    const totalWeight = records
      .filter((r) => r.weight)
      .reduce((sum, r) => sum + r.weight, 0);
    const weightRecords = records.filter((r) => r.weight).length;

    const averages = {
      steps: Math.round(totalSteps / records.length),
      sleep: parseFloat((totalSleep / records.length).toFixed(1)),
      calories: Math.round(totalCalories / records.length),
      water: parseFloat((totalWater / records.length).toFixed(1)),
      weight:
        weightRecords > 0
          ? parseFloat((totalWeight / weightRecords).toFixed(1))
          : 0,
    };

    // Calculate trends (compare first half vs second half)
    const midPoint = Math.floor(records.length / 2);
    const firstHalf = records.slice(0, midPoint);
    const secondHalf = records.slice(midPoint);

    const calculateHalfAverage = (half, field) => {
      const sum = half.reduce((s, r) => {
        if (field === "sleep") return s + (r.sleep?.duration || 0);
        if (field === "calories") return s + (r.calories?.intake || 0);
        return s + (r[field] || 0);
      }, 0);
      return sum / half.length;
    };

    const trends = {
      steps:
        calculateHalfAverage(secondHalf, "steps") -
        calculateHalfAverage(firstHalf, "steps"),
      sleep:
        calculateHalfAverage(secondHalf, "sleep") -
        calculateHalfAverage(firstHalf, "sleep"),
      calories:
        calculateHalfAverage(secondHalf, "calories") -
        calculateHalfAverage(firstHalf, "calories"),
      water:
        calculateHalfAverage(secondHalf, "water") -
        calculateHalfAverage(firstHalf, "water"),
    };

    // Weight change
    const weightData = records.filter((r) => r.weight).map((r) => r.weight);
    const weightChange =
      weightData.length >= 2
        ? weightData[weightData.length - 1] - weightData[0]
        : 0;

    // Calculate goal achievement
    const goals = userData.healthInfo?.goals || {};
    const goalAchievement = {
      steps: goals.steps?.daily
        ? (averages.steps / goals.steps.daily) * 100
        : 0,
      sleep: goals.sleep?.daily
        ? (averages.sleep / goals.sleep.daily) * 100
        : 0,
      water: goals.water?.daily
        ? (averages.water / goals.water.daily) * 100
        : 0,
    };

    // Find best and worst days
    const bestStepsDay = records.reduce(
      (max, r) => (r.steps > (max.steps || 0) ? r : max),
      {}
    );
    const bestSleepDay = records.reduce(
      (max, r) =>
        (r.sleep?.duration || 0) > (max.sleep?.duration || 0) ? r : max,
      {}
    );

    // Calculate mood analytics
    const moodCounts = {
      happy: 0,
      sad: 0,
      angry: 0,
      disgust: 0,
      bored: 0,
    };

    records.forEach((r) => {
      if (r.mood && moodCounts.hasOwnProperty(r.mood)) {
        moodCounts[r.mood]++;
      }
    });

    const totalMoodEntries = Object.values(moodCounts).reduce(
      (sum, count) => sum + count,
      0
    );
    const moodPercentages = {};
    Object.keys(moodCounts).forEach((mood) => {
      moodPercentages[mood] =
        totalMoodEntries > 0
          ? Math.round((moodCounts[mood] / totalMoodEntries) * 100)
          : 0;
    });

    const mostCommonMood = Object.keys(moodCounts).reduce((a, b) =>
      moodCounts[a] > moodCounts[b] ? a : b
    );

    return {
      summary: {
        totalDays: parseInt(timeRange),
        trackedDays: records.length,
        streak: userData.healthInfo?.dailyTracking?.currentStreak || 0,
        longestStreak: userData.healthInfo?.dailyTracking?.longestStreak || 0,
      },
      averages,
      trends,
      weightChange,
      goalAchievement,
      bestDays: {
        steps: bestStepsDay,
        sleep: bestSleepDay,
      },
      moodAnalytics: {
        counts: moodCounts,
        percentages: moodPercentages,
        mostCommon: mostCommonMood,
        totalEntries: totalMoodEntries,
      },
      records,
    };
  };

  const MetricCard = ({
    title,
    value,
    unit,
    trend,
    icon: Icon,
    color,
    goalPercent,
  }) => {
    const trendPositive = trend > 0;
    const TrendIcon = trendPositive ? TrendingUp : TrendingDown;

    return (
      <div className="bg-[#1a2f2f] rounded-2xl p-5 border border-[#2a3f3f]">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ backgroundColor: color + "20" }}
            >
              <Icon className="w-5 h-5" style={{ color }} />
            </div>
            <h3 className="text-gray-400 text-sm">{title}</h3>
          </div>
          {trend !== undefined && trend !== 0 && (
            <div
              className={`flex items-center gap-1 ${
                trendPositive ? "text-[#00ff88]" : "text-[#ff6644]"
              }`}
            >
              <TrendIcon className="w-4 h-4" />
              <span className="text-xs font-medium">
                {Math.abs(trend).toFixed(1)}
              </span>
            </div>
          )}
        </div>

        <div className="mb-3">
          <div className="flex items-baseline gap-2">
            <span className="text-3xl font-bold text-white">{value}</span>
            <span className="text-gray-400 text-sm">{unit}</span>
          </div>
          {goalPercent !== undefined && (
            <p className="text-xs text-gray-500 mt-1">
              {goalPercent.toFixed(0)}% of goal
            </p>
          )}
        </div>

        {goalPercent !== undefined && (
          <div className="h-1.5 bg-[#0a1f1f] rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${Math.min(goalPercent, 100)}%`,
                backgroundColor:
                  goalPercent >= 100
                    ? "#00ff88"
                    : goalPercent >= 70
                    ? color
                    : "#ffaa00",
              }}
            />
          </div>
        )}
      </div>
    );
  };

  const MiniChart = ({ data, color, label }) => {
    if (!data || data.length === 0) return null;

    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;

    return (
      <div className="bg-[#1a2f2f] rounded-xl p-4 border border-[#2a3f3f]">
        <h4 className="text-sm text-gray-400 mb-3">{label}</h4>
        <div className="flex items-end gap-1 h-24">
          {data.slice(-14).map((value, index) => {
            const height = ((value - min) / range) * 100;
            return (
              <div
                key={index}
                className="flex-1 rounded-t transition-all hover:opacity-80 cursor-pointer"
                style={{
                  height: `${Math.max(height, 5)}%`,
                  backgroundColor: color,
                  opacity: 0.7 + (height / 100) * 0.3,
                }}
                title={`${value}`}
              />
            );
          })}
        </div>
        <div className="flex justify-between mt-2 text-xs text-gray-500">
          <span>14 days ago</span>
          <span>Today</span>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#0a1f1f] flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#00d9ff] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a1f1f] pb-24">
      <div className="w-full max-w-4xl mx-auto">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[#0a1f1f] border-b border-[#1a2f2f] px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate("/tracker")}
                className="p-2 hover:bg-[#1a2f2f] rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-400" />
              </button>
              <div>
                <h1 className="text-xl font-bold text-white">Analytics</h1>
                <p className="text-sm text-gray-400">
                  {timeRange} days overview
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Time Range Selector */}
        <div className="px-5 py-4 border-b border-[#1a2f2f]">
          <div className="flex gap-2">
            {["7", "30", "90"].map((days) => (
              <button
                key={days}
                onClick={() => setTimeRange(days)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  timeRange === days
                    ? "bg-[#00d9ff] text-[#0a1f1f]"
                    : "bg-[#1a2f2f] text-gray-400 hover:bg-[#2a3f3f]"
                }`}
              >
                {days} Days
              </button>
            ))}
          </div>
        </div>

        {/* Summary Cards */}
        <div className="px-5 py-4">
          <h2 className="text-lg font-semibold text-white mb-4">Summary</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-[#1a2f2f] rounded-xl p-4 border border-[#2a3f3f]">
              <Calendar className="w-5 h-5 text-[#00d9ff] mb-2" />
              <p className="text-2xl font-bold text-white">
                {analyticsData?.summary.trackedDays}
              </p>
              <p className="text-xs text-gray-400">Days Tracked</p>
            </div>
            <div className="bg-[#1a2f2f] rounded-xl p-4 border border-[#2a3f3f]">
              <Target className="w-5 h-5 text-[#00ff88] mb-2" />
              <p className="text-2xl font-bold text-white">
                {analyticsData?.summary.streak}
              </p>
              <p className="text-xs text-gray-400">Current Streak</p>
            </div>
            <div className="bg-[#1a2f2f] rounded-xl p-4 border border-[#2a3f3f]">
              <Award className="w-5 h-5 text-[#ffaa00] mb-2" />
              <p className="text-2xl font-bold text-white">
                {analyticsData?.summary.longestStreak}
              </p>
              <p className="text-xs text-gray-400">Longest Streak</p>
            </div>
            <div className="bg-[#1a2f2f] rounded-xl p-4 border border-[#2a3f3f]">
              <Scale className="w-5 h-5 text-[#ff88ff] mb-2" />
              <p className="text-2xl font-bold text-white">
                {analyticsData?.weightChange > 0 ? "+" : ""}
                {analyticsData?.weightChange.toFixed(1)}
              </p>
              <p className="text-xs text-gray-400">Weight Change (kg)</p>
            </div>
          </div>
        </div>

        {/* Average Metrics */}
        <div className="px-5 py-4">
          <h2 className="text-lg font-semibold text-white mb-4">
            Average Metrics
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MetricCard
              title="Daily Steps"
              value={analyticsData?.averages.steps || 0}
              unit="steps"
              trend={analyticsData?.trends.steps}
              icon={Activity}
              color="#00d9ff"
              goalPercent={analyticsData?.goalAchievement.steps}
            />
            <MetricCard
              title="Sleep Duration"
              value={analyticsData?.averages.sleep || 0}
              unit="hours"
              trend={analyticsData?.trends.sleep}
              icon={Moon}
              color="#ff88ff"
              goalPercent={analyticsData?.goalAchievement.sleep}
            />
            <MetricCard
              title="Calorie Intake"
              value={analyticsData?.averages.calories || 0}
              unit="kcal"
              trend={analyticsData?.trends.calories}
              icon={Flame}
              color="#ff6644"
            />
            <MetricCard
              title="Water Intake"
              value={analyticsData?.averages.water || 0}
              unit="liters"
              trend={analyticsData?.trends.water}
              icon={Droplets}
              color="#00d9ff"
              goalPercent={analyticsData?.goalAchievement.water}
            />
          </div>
        </div>

        {/* Trend Charts */}
        <div className="px-5 py-4">
          <h2 className="text-lg font-semibold text-white mb-4">
            Trends (Last 14 Days)
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MiniChart
              data={analyticsData?.records.slice(-14).map((r) => r.steps || 0)}
              color="#00d9ff"
              label="Steps"
            />
            <MiniChart
              data={analyticsData?.records
                .slice(-14)
                .map((r) => r.sleep?.duration || 0)}
              color="#ff88ff"
              label="Sleep (hours)"
            />
            <MiniChart
              data={analyticsData?.records.slice(-14).map((r) => r.water || 0)}
              color="#00d9ff"
              label="Water (L)"
            />
            <MiniChart
              data={analyticsData?.records
                .slice(-14)
                .map((r) => r.calories?.intake || 0)}
              color="#ff6644"
              label="Calories"
            />
          </div>
        </div>

        {/* Mood Tracking Analytics */}
        {analyticsData?.moodAnalytics &&
          analyticsData.moodAnalytics.totalEntries > 0 && (
            <div className="px-5 py-4">
              <h2 className="text-lg font-semibold text-white mb-4">
                Mood Tracking
              </h2>

              {/* Mood Summary */}
              <div className="bg-[#1a2f2f] rounded-2xl p-5 border border-[#2a3f3f] mb-4">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-gray-400">Most Common Mood</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-3xl">
                        {analyticsData.moodAnalytics.mostCommon === "happy" &&
                          "😊"}
                        {analyticsData.moodAnalytics.mostCommon === "sad" &&
                          "😢"}
                        {analyticsData.moodAnalytics.mostCommon === "angry" &&
                          "😠"}
                        {analyticsData.moodAnalytics.mostCommon === "disgust" &&
                          "🤢"}
                        {analyticsData.moodAnalytics.mostCommon === "bored" &&
                          "😐"}
                      </span>
                      <span className="text-xl font-semibold text-white capitalize">
                        {analyticsData.moodAnalytics.mostCommon}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-400">Total Entries</p>
                    <p className="text-2xl font-bold text-white">
                      {analyticsData.moodAnalytics.totalEntries}
                    </p>
                  </div>
                </div>

                {/* Mood Distribution */}
                <div className="space-y-3">
                  {Object.entries(analyticsData.moodAnalytics.counts).map(
                    ([mood, count]) => {
                      const moodData = {
                        happy: {
                          emoji: "😊",
                          color: "#fbbf24",
                          label: "Happy",
                        },
                        sad: { emoji: "😢", color: "#60a5fa", label: "Sad" },
                        angry: {
                          emoji: "😠",
                          color: "#f87171",
                          label: "Angry",
                        },
                        disgust: {
                          emoji: "🤢",
                          color: "#86efac",
                          label: "Disgust",
                        },
                        bored: {
                          emoji: "😐",
                          color: "#fde047",
                          label: "Bored",
                        },
                      };

                      const percentage =
                        analyticsData.moodAnalytics.percentages[mood];

                      return (
                        <div key={mood} className="flex items-center gap-3">
                          <span className="text-2xl">
                            {moodData[mood].emoji}
                          </span>
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm text-gray-300 capitalize">
                                {moodData[mood].label}
                              </span>
                              <span className="text-sm text-gray-400">
                                {count} days ({percentage}%)
                              </span>
                            </div>
                            <div className="h-2 bg-[#0a1f1f] rounded-full overflow-hidden">
                              <div
                                className="h-full rounded-full transition-all"
                                style={{
                                  width: `${percentage}%`,
                                  backgroundColor: moodData[mood].color,
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    }
                  )}
                </div>
              </div>

              {/* 30-Day Mood Calendar */}
              <div className="bg-[#1a2f2f] rounded-2xl p-5 border border-[#2a3f3f]">
                <h3 className="text-white font-medium mb-4">
                  30-Day Mood Calendar
                </h3>
                <div className="grid grid-cols-7 gap-2">
                  {analyticsData.records.slice(-30).map((record, index) => {
                    const moodEmojis = {
                      happy: "😊",
                      sad: "😢",
                      angry: "😠",
                      disgust: "🤢",
                      bored: "😐",
                    };

                    const moodColors = {
                      happy: "#fbbf24",
                      sad: "#60a5fa",
                      angry: "#f87171",
                      disgust: "#86efac",
                      bored: "#fde047",
                    };

                    return (
                      <div
                        key={index}
                        className="aspect-square rounded-lg flex items-center justify-center text-2xl"
                        style={{
                          backgroundColor: record.mood
                            ? moodColors[record.mood] + "30"
                            : "#0a1f1f",
                          border: record.mood
                            ? `2px solid ${moodColors[record.mood]}`
                            : "2px solid #2a3f3f",
                        }}
                        title={`${new Date(
                          record.date
                        ).toLocaleDateString()}: ${
                          record.mood || "No mood tracked"
                        }`}
                      >
                        {record.mood ? moodEmojis[record.mood] : ""}
                      </div>
                    );
                  })}
                </div>
                <p className="text-xs text-gray-500 mt-3 text-center">
                  Last {Math.min(analyticsData.records.length, 30)} days
                </p>
              </div>
            </div>
          )}

        {/* Best Days */}
        {analyticsData?.bestDays && (
          <div className="px-5 py-4">
            <h2 className="text-lg font-semibold text-white mb-4">Best Days</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {analyticsData.bestDays.steps?.steps && (
                <div className="bg-[#1a2f2f] rounded-xl p-4 border border-[#2a3f3f]">
                  <div className="flex items-center gap-2 mb-2">
                    <Activity className="w-5 h-5 text-[#00d9ff]" />
                    <h3 className="text-white font-medium">Most Steps</h3>
                  </div>
                  <p className="text-2xl font-bold text-white mb-1">
                    {analyticsData.bestDays.steps.steps.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-400">
                    {new Date(
                      analyticsData.bestDays.steps.date
                    ).toLocaleDateString()}
                  </p>
                </div>
              )}
              {analyticsData.bestDays.sleep?.sleep?.duration && (
                <div className="bg-[#1a2f2f] rounded-xl p-4 border border-[#2a3f3f]">
                  <div className="flex items-center gap-2 mb-2">
                    <Moon className="w-5 h-5 text-[#ff88ff]" />
                    <h3 className="text-white font-medium">Best Sleep</h3>
                  </div>
                  <p className="text-2xl font-bold text-white mb-1">
                    {analyticsData.bestDays.sleep.sleep.duration}h
                  </p>
                  <p className="text-xs text-gray-400">
                    {new Date(
                      analyticsData.bestDays.sleep.date
                    ).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackerAnalytics;
